<template>
  <v-card light class="ad-chat" flat>
    <v-card-title class="ad-chat__header">
      <v-icon color="primary" class="mr-2">mdi-creation</v-icon>
      Ad-Chat
      <v-spacer />
      <v-select
        v-model="selectedModel"
        :items="modelOptions"
        class="ad-chat__model-select"
        dense
        outlined
        hide-details
      />
      <v-btn v-if="messages.length" class="ml-1" icon small title="Chat leeren" @click="resetChat">
        <v-icon small>mdi-delete-outline</v-icon>
      </v-btn>
      <v-btn v-if="collapsible" icon small title="Chat einklappen" @click="$emit('collapse')">
        <v-icon small>mdi-chevron-double-right</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="ad-chat__messages" ref="messageContainer">
      <div v-if="!messages.length" class="ad-chat__empty">
        <div class="mb-2">Stell Fragen zu den Werbedaten des gewählten Zeitraums oder entwickle neue Anzeigen.</div>
        <v-chip class="ma-1" small color="primary" outlined @click="runAnalysis">
          <v-icon x-small left>mdi-brain</v-icon>
          Vollständige Analyse erstellen
        </v-chip>
        <v-chip
          v-for="suggestion in suggestions"
          :key="suggestion"
          class="ma-1"
          small
          outlined
          @click="input = suggestion"
        >{{ suggestion }}</v-chip>
      </div>

      <div
        v-for="(message, index) in messages"
        :key="index"
        class="ad-chat__message"
        :class="message.role === 'user' ? 'ad-chat__message--user' : 'ad-chat__message--assistant'"
      >
        <div class="ad-chat__bubble">
          <div v-if="messageImages(message).length" class="ad-chat__images">
            <img
              v-for="(image, imageIndex) in messageImages(message)"
              :key="imageIndex"
              :src="`data:${image.source.media_type};base64,${image.source.data}`"
              class="ad-chat__image"
            />
          </div>
          <div v-html="renderMarkdown(messageText(message))"></div>
          <div v-if="message.answeredBy && message.requestedModel && message.answeredBy !== message.requestedModel" class="ad-chat__fallback-hint">
            Fallback: beantwortet von {{ modelLabel(message.answeredBy) }}
          </div>
        </div>
      </div>

      <div v-if="sending" class="ad-chat__message ad-chat__message--assistant">
        <div class="ad-chat__bubble ad-chat__bubble--thinking">
          <v-progress-circular indeterminate size="14" width="2" class="mr-2" />
          {{ modelLabel(selectedModel) }} denkt nach…
        </div>
      </div>

      <v-alert v-if="chatError" type="error" dense outlined class="mt-2">
        {{ chatError }} <v-btn x-small text color="error" @click="retryLast">Erneut senden</v-btn>
      </v-alert>
    </v-card-text>

    <v-card-actions class="ad-chat__input-area">
      <div class="ad-chat__attachments" v-if="pendingImages.length">
        <div v-for="(image, index) in pendingImages" :key="index" class="ad-chat__attachment">
          <img :src="`data:${image.media_type};base64,${image.data}`" />
          <v-btn icon x-small class="ad-chat__attachment-remove" @click="pendingImages.splice(index, 1)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="ad-chat__input-row">
        <v-btn v-if="messages.length" icon :disabled="sending" title="Vollständige Analyse erstellen" @click="runAnalysis">
          <v-icon>mdi-brain</v-icon>
        </v-btn>
        <v-btn icon :disabled="sending" @click="$refs.fileInput.click()" title="Bild anhängen (z.B. Creative-Entwurf)">
          <v-icon>mdi-image-plus</v-icon>
        </v-btn>
        <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="onFilesSelected" />
        <v-textarea
          v-model="input"
          class="ad-chat__textarea"
          placeholder="Frage stellen oder neue Anzeige besprechen…"
          rows="1"
          auto-grow
          hide-details
          dense
          outlined
          :disabled="sending"
          @keydown.enter.exact.prevent="send"
        />
        <v-btn icon color="primary" :disabled="sending || (!input.trim() && !pendingImages.length)" @click="send">
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
      <div v-if="!insights" class="ad-chat__hint">Werbedaten werden geladen — der Chat kennt sie, sobald sie da sind.</div>
    </v-card-actions>
  </v-card>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { mapGetters } from "vuex";
import { buildAdDataPayload } from "@/components/Statistics/StatisticByShops/adDataPayload";
import { renderMarkdownLite } from "@/components/Statistics/StatisticByShops/markdownLite";

const MAX_IMAGE_EDGE = 1568;

export default {
  name: "AdInsightsChat",
  props: {
    shopCode: String,
    insights: {
      type: Object,
      default: null,
    },
    breakEvenRoas: {
      type: Number,
      default: null,
    },
    shopRevenue: {
      type: Number,
      default: null,
    },
    shopProfit: {
      type: Number,
      default: null,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messages: [],
      input: "",
      pendingImages: [],
      sending: false,
      chatError: null,
      lastFailedMessage: null,
      selectedModel: "claude-fable-5",
      modelOptions: [
        { text: "Fable 5", value: "claude-fable-5" },
        { text: "Opus 4.8", value: "claude-opus-4-8" },
        { text: "Sonnet 5", value: "claude-sonnet-5" },
      ],
      suggestions: [
        "Welche 3 Anzeigen sollte ich zuerst optimieren?",
        "Schreib mir 5 neue Hooks basierend auf den Top-Performern",
        "Wo verliere ich gerade am meisten Geld?",
      ],
    };
  },
  computed: {
    ...mapGetters(["statisticRange"]),
    adData() {
      return buildAdDataPayload(this.insights);
    },
  },
  watch: {
    shopCode() {
      this.resetChat();
    },
  },
  methods: {
    resetChat() {
      this.messages = [];
      this.chatError = null;
      this.pendingImages = [];
    },
    messageText(message) {
      if (typeof message.content === "string") return message.content;
      return message.content
        .filter((block) => block.type === "text")
        .map((block) => block.text)
        .join("\n");
    },
    messageImages(message) {
      if (typeof message.content === "string") return [];
      return message.content.filter((block) => block.type === "image");
    },
    renderMarkdown(text) {
      return renderMarkdownLite(text);
    },
    async onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      for (const file of files) {
        const resized = await this.resizeImage(file);
        this.pendingImages.push(resized);
      }
    },
    resizeImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          resolve({
            media_type: "image/jpeg",
            data: dataUrl.split(",")[1],
          });
        };
        img.onerror = reject;
        img.src = url;
      });
    },
    buildUserContent() {
      const blocks = this.pendingImages.map((image) => ({
        type: "image",
        source: { type: "base64", media_type: image.media_type, data: image.data },
      }));
      if (this.input.trim()) {
        blocks.push({ type: "text", text: this.input.trim() });
      }
      return blocks;
    },
    async send() {
      if (this.sending || (!this.input.trim() && !this.pendingImages.length)) return;

      const userMessage = { role: "user", content: this.buildUserContent() };
      this.messages.push(userMessage);
      this.input = "";
      this.pendingImages = [];
      await this.sendMessages();
    },
    async retryLast() {
      this.chatError = null;
      await this.sendMessages();
    },
    modelLabel(modelId) {
      const option = this.modelOptions.find((item) => modelId?.startsWith(item.value));
      return option ? option.text : modelId;
    },
    // The deep analysis (Opus 4.8 + 7-day trends) appears as a normal chat
    // exchange, so follow-up questions can reference it.
    async runAnalysis() {
      if (this.sending) return;
      this.messages.push({
        role: "user",
        content: [{ type: "text", text: "Erstelle mir eine vollständige Analyse der Werbedaten mit priorisierten Maßnahmen." }],
      });
      this.sending = true;
      this.chatError = null;
      this.scrollDown();
      try {
        const analyze = httpsCallable(functions, "analyzeAdInsights", { timeout: 300000 });
        const res = await analyze({
          shopCode: this.shopCode,
          created_at_min: this.statisticRange.min,
          created_at_max: this.statisticRange.max,
          shopContext: {
            breakEvenRoas: this.breakEvenRoas,
            umsatz: this.shopRevenue,
            gewinn: this.shopProfit,
          },
        });
        this.messages.push({
          role: "assistant",
          content: [{ type: "text", text: res.data.analysis }],
        });
      } catch (e) {
        this.chatError = "Analyse fehlgeschlagen.";
        this.messages.pop();
      }
      this.sending = false;
      this.scrollDown();
    },
    async sendMessages() {
      this.sending = true;
      this.chatError = null;
      this.scrollDown();
      try {
        const chat = httpsCallable(functions, "adInsightsChat", { timeout: 300000 });
        const res = await chat({
          // Strip UI-only fields (answeredBy etc.) — the API accepts only role/content.
          messages: this.messages.map(({ role, content }) => ({ role, content })),
          adData: this.adData,
          model: this.selectedModel,
          shopContext: {
            shop: this.shopCode,
            zeitraum: { von: this.statisticRange.min, bis: this.statisticRange.max },
            breakEvenRoas: this.breakEvenRoas,
            umsatz: this.shopRevenue,
            gewinn: this.shopProfit,
          },
        });
        // Store the full content blocks — they are echoed back verbatim on the
        // next turn (thinking blocks must be replayed unmodified).
        this.messages.push({
          role: "assistant",
          content: res.data.content,
          answeredBy: res.data.model,
          requestedModel: res.data.requestedModel,
        });
      } catch (e) {
        this.chatError = e?.message || "Senden fehlgeschlagen.";
      }
      this.sending = false;
      this.scrollDown();
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.messageContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 820px;
  border-radius: 14px !important;
  box-shadow: rgba(14, 63, 126, 0.05) 0 0 0 1px, rgba(42, 51, 70, 0.05) 0 4px 12px -2px !important;
}

.ad-chat__header {
  padding: 12px 16px;
  font-size: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.ad-chat__model-select {
  max-width: 140px;
  font-size: 13px;
}

.ad-chat__fallback-hint {
  margin-top: 4px;
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
}

.ad-chat__messages {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ad-chat__empty {
  color: #757575;
  font-size: 13px;
  text-align: center;
  margin-top: 24px;
}

.ad-chat__message {
  display: flex;

  &--user {
    justify-content: flex-end;
  }

  &--assistant {
    justify-content: flex-start;
  }
}

.ad-chat__bubble {
  max-width: 88%;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;

  .ad-chat__message--user & {
    background-color: #e3eefb;
  }

  .ad-chat__message--assistant & {
    background-color: #f4f2fa;
  }

  &--thinking {
    display: flex;
    align-items: center;
    color: #757575;
  }
}

.ad-chat__bubble ::v-deep {
  .md-heading {
    font-weight: 700;
    margin: 8px 0 2px;
  }

  .md-space {
    height: 8px;
  }

  .md-list {
    margin: 4px 0;
    padding-left: 18px;

    li {
      margin: 2px 0;
    }
  }

  .md-table {
    border-collapse: collapse;
    width: 100%;
    margin: 6px 0;
    font-size: 12px;
    display: block;
    overflow-x: auto;

    th, td {
      border: 1px solid #ddd4f0;
      padding: 4px 8px;
      text-align: left;
      white-space: nowrap;
    }

    th {
      background-color: rgba(255, 255, 255, 0.6);
      font-weight: 700;
    }

    tr:nth-child(even) td {
      background-color: rgba(255, 255, 255, 0.45);
    }
  }

  code {
    background-color: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 1px 4px;
    font-size: 12px;
  }
}

.ad-chat__images {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.ad-chat__image {
  max-width: 140px;
  max-height: 140px;
  border-radius: 6px;
  object-fit: cover;
}

.ad-chat__input-area {
  flex-direction: column;
  align-items: stretch;
  padding: 8px 12px 12px;
  border-top: 1px solid #ececec;
}

.ad-chat__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.ad-chat__attachment {
  position: relative;

  img {
    width: 52px;
    height: 52px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #dddddd;
  }
}

.ad-chat__attachment-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: white !important;
  border: 1px solid #dddddd;
}

.ad-chat__input-row {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.ad-chat__textarea {
  flex: 1;
}

.ad-chat__hint {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 4px;
}
</style>
